import { useCallback, useEffect, useRef, useState } from 'react';

export type RecorderState = 'idle' | 'recording' | 'recorded';

export interface UseRecorderResult {
  state: RecorderState;
  blob: Blob | null;
  url: string | null;
  error: 'mic_denied' | 'unsupported' | null;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
}

/** Minimal MediaRecorder wrapper: one recording at a time, kept in memory. */
export default function useRecorder(): UseRecorderResult {
  const [state, setState] = useState<RecorderState>('idle');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<UseRecorderResult['error']>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const releaseUrl = useCallback((): void => {
    setUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  useEffect(
    () => () => {
      recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
      releaseUrl();
    },
    [releaseUrl],
  );

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('unsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e: BlobEvent): void => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = (): void => {
        stream.getTracks().forEach((track) => track.stop());
        const recorded = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        releaseUrl();
        setBlob(recorded);
        setUrl(URL.createObjectURL(recorded));
        setState('recorded');
      };
      recorderRef.current = recorder;
      releaseUrl();
      setBlob(null);
      recorder.start();
      setState('recording');
    } catch (err) {
      console.error('[school] getUserMedia failed', err);
      setError('mic_denied');
      setState('idle');
    }
  }, [releaseUrl]);

  const stop = useCallback((): void => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const reset = useCallback((): void => {
    releaseUrl();
    setBlob(null);
    setState('idle');
  }, [releaseUrl]);

  return { state, blob, url, error, start, stop, reset };
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = (): void => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = (): void => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
